import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { PlusOutlined } from "@ant-design/icons";
import {
  Table,
  Typography,
  Spin,
  Alert,
  Button,
  Modal,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
} from 'antd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const { Title, Paragraph } = Typography;
const { Search, TextArea } = Input;
const { Option } = Select;
const DATE_FORMAT = 'DD-MM-YYYY';

const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);


function DemoLinks() {

 const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  // Auth verification (same pattern used elsewhere)
  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );
        if (!res?.data?.success) {
          toast.error(res?.data?.message || 'Please login again.');
          localStorage.clear();
          navigate('/login');
        }
      } catch (err) {
        toast.error('Session verification failed. Please login again.');
        localStorage.clear();
        navigate('/login');
      }
    };
    verifyUser();
  }, [navigate, location.pathname, token, email, role]); 

  // state
  const [demoLinks, setDemoLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // add demo modal state
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // form fields for add modal
  const [demoDate, setDemoDate] = useState(null);
  const [timeText, setTimeText] = useState(''); // "06:00 PM to 08:00 PM"
  const [batchStartDate, setBatchStartDate] = useState(null);
  const [batchDay, setBatchDay] = useState(null); // Morning/Evening
  const [batchTime, setBatchTime] = useState(''); // "07:00 AM to 08:00 AM"
  const [demoLink, setDemoLink] = useState('');
  const [demoRecordingLink, setDemoRecordingLink] = useState('');

  // filters
  const [filterMonth, setFilterMonth] = useState(null); // 'JANUARY' etc
  const [searchText, setSearchText] = useState(''); // optional quick search across DemoLink text

  // Fetch demolinks
  const fetchDemoLinks = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${process.env.REACT_APP_API_URL}/demolinks/all-demolinks`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      
      // console.log(res.data.data);
      
      const normalized = (Array.isArray(data) ? data : []).map((r, i) => ({
        key: r.id ?? `${i}`,
        ...r,
      }));
      setDemoLinks(normalized);
    } catch (err) {
      console.error('Failed to fetch demo links', err);
      setError('Failed to load demo links');
      toast.error('Failed to load demo links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemoLinks();
  }, [token, location.key]);


  // action modal state
const [actionModalVisible, setActionModalVisible] = useState(false);
const [selectedDemo, setSelectedDemo] = useState(null);
const [deleteLoading, setDeleteLoading] = useState(false);

const openActionModal = (record) => {
  setSelectedDemo(record);
  setActionModalVisible(true);
};


  // Table columns
  const columns = [
    {
  title: <span className="text-[17px] font-bold text-white">Demo Date</span>,
  dataIndex: 'DemoDate',
  key: 'DemoDate',
  width: 190,
  render: (d, record) => (
    <button
      onClick={() => openEditModal(record)}
      className="ml-10 font-bold text-left text-blue-600 underline whitespace-nowrap"
      type="button"
    >
      {safe(d)}
    </button>
  )
},

    // { title: 'Day', dataIndex: 'Day', key: 'Day', width: 120, render: (t) => <span className="whitespace-nowrap">{safe(t)}</span> },
    { title: <span className="text-[17px] font-bold text-white">Month</span>, dataIndex: 'Month', key: 'Month', width: 120, render: (t) => <span className="ml-1 font-bold text-cyan-700 whitespace-nowrap">{safe(t)}</span> },
    // { title: 'Year', dataIndex: 'Year', key: 'Year', width: 110, render: (t) => <span className="whitespace-nowrap">{safe(t)}</span> },
    { title: <span className="text-[17px] font-bold text-white">Time</span>, dataIndex: 'Time', key: 'Time', width: 200, render: (t) => <span className="ml-3 whitespace-nowrap">{safe(t)}</span> },
    { title: <span className="text-[17px] font-bold text-white">Batch Start Date</span>, dataIndex: 'BatchStartDate', key: 'BatchStartDate', width: 180, render: (d) => <span className="font-bold text-green-600 whitespace-nowrap ml-9">{safe(d)}</span> },
    { title: <span className="text-[17px] font-bold text-white">Batch Day</span>, dataIndex: 'BatchDay', key: 'BatchDay', width: 120, render: (t) => <span className="ml-2 font-bold text-orange-600 whitespace-nowrap">{safe(t)}</span> },
    { title: <span className="text-[17px] font-bold text-white">Batch Time</span>, dataIndex: 'BatchTime', key: 'BatchTime', width: 180, render: (t) => <span className="ml-1 font-bold text-violet-700 whitespace-nowrap">{safe(t)}</span> },
    {
      title: <span className="text-[17px] font-bold text-white">Demo Link</span>,
      dataIndex: 'DemoLink',
      key: 'DemoLink',
      width: 320,
      render: (t) => (t ? <a className="truncate inline-block max-w-[300px] whitespace-nowrap" href={t} target="_blank" rel="noreferrer">{t}</a> : <span>-</span>),
    },
    {
      title: <span className="text-[17px] font-bold text-white">Demo Recording Link</span>,
      dataIndex: 'DemoRecordingLink',
      key: 'DemoRecordingLink',
      width: 320,
      render: (t) => (t ? <a className="truncate inline-block max-w-[300px] whitespace-nowrap" href={t} target="_blank" rel="noreferrer">{t}</a> : <span>-</span>),
    },

    {
  title: (
    <span className="font-bold text-[17px] text-white whitespace-nowrap">
      Action
    </span>
  ),
  key: 'action',
  fixed: 'right',
  width: 200,
  align: 'center',
  render: (_, record) => {
    return (
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => openActionModal(record)}
          className="px-4 py-2 font-semibold text-white transition-colors duration-200 rounded-md shadow whitespace-nowrap bg-gradient-to-r from-blue-600 to-blue-500 hover:from-red-700 hover:to-rose-600"
          type="button"
        >
          Actions
        </button>
      </div>
    );
  },
},

  ];

  // Month options
  const months = [
    'JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'
  ];

  // helper to extract month token from available fields (DemoDate or Month)
  const extractMonthFromRow = (r) => {
    // prefer r.Month if present and alphabetic
    if (r?.Month) {
      return String(r.Month).toUpperCase();
    }
    // try DemoDate 'DD-MM-YYYY' or 'DD/MM/YYYY' or ISO
    if (r?.DemoDate) {
      const dateStr = String(r.DemoDate).trim();
      // try parse known formats with dayjs
      const dt = dayjs(dateStr, [DATE_FORMAT, 'DD/MM/YYYY', 'YYYY-MM-DD', dayjs.ISO_8601], true);
      if (dt.isValid()) return dt.format('MMMM').toUpperCase();
      // fallback: try splitting and find alphabetic token
      const parts = dateStr.split(/[^A-Za-z]+/).filter(Boolean);
      if (parts.length) return parts[0].toUpperCase();
    }
    return '';
  };

  // filtered list
  const filtered = useMemo(() => {
    return demoLinks.filter((r) => {
      if (filterMonth) {
        const m = extractMonthFromRow(r);
        if (!m) return false;
        if (m.toUpperCase() !== filterMonth.toUpperCase()) return false;
      }
      if (searchText) {
        const s = searchText.toLowerCase();
        const hay = `${r.DemoLink || ''} ${r.DemoRecordingLink || ''} ${r.Time || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [demoLinks, filterMonth, searchText]);

  // Open add modal -> reset fields
  const openAddModal = () => {
    setDemoDate(null);
    setTimeText('');
    setBatchStartDate(null);
   
    setBatchDay(null);
    setBatchTime('');
    setDemoLink('');
    setDemoRecordingLink('');
    setAddModalVisible(true);
  };

  const validateAddForm = () => {
    if (!demoDate || !demoDate.isValid()) {
      toast.error('Select Demo Date');
      return false;
    }
    if (!timeText || !timeText.trim()) {
      toast.error('Enter Time range (e.g. 06:00 PM to 08:00 PM)');
      return false;
    }
    if (!batchStartDate || !batchStartDate.isValid()) {
      toast.error('Select Batch Start Date');
      return false;
    }
    
    if (!batchDay) {
      toast.error('Select Batch Day (Morning/Evening)');
      return false;
    }
    if (!batchTime || !batchTime.trim()) {
      toast.error('Enter Batch Time (e.g. 07:00 AM to 08:00 AM)');
      return false;
    }
    if (!demoLink || !demoLink.trim()) {
      toast.error('DemoLink is required');
      return false;
    }
    return true;
  };

  // POST add demo
  const handleAddDemo = async () => {
    if (!validateAddForm()) return;
    setAddLoading(true);

    const payload = {
      DemoDate: demoDate.format(DATE_FORMAT), // '21-12-2025'
      Time: timeText,
      BatchStartDate: batchStartDate.format(DATE_FORMAT),
      BatchDay: batchDay,
      BatchTime: batchTime,
      DemoLink: demoLink,
      DemoRecordingLink: demoRecordingLink || undefined,
    };

    try {
      const url = `${process.env.REACT_APP_API_URL}/demolinks/add-demo-dates`;
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 20000,
      });

      if (res?.data?.success || res?.status === 200 || res?.status === 201) {

        
        
        toast.success(res?.data?.message || 'Demo added');
        setAddModalVisible(false);
        // refresh table
        await fetchDemoLinks();
      } else {
        toast.error(res?.data?.message || 'Failed to add demo');
      }
    } catch (err) {
      console.error('Add demo error', err);
      const msg = err?.response?.data?.message || err?.message || 'Network/server error';
      toast.error(msg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteDemo = async () => {
  if (!selectedDemo) return;
  setDeleteLoading(true);
  try {
    const url = `${process.env.REACT_APP_API_URL}/demolinks/delete-demolink`;

    const res = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      data: { id: selectedDemo.id }, // ✅ send ID in req.body
    });

    if (res?.data?.success || res?.status === 200) {
      toast.success(res?.data?.message || 'Demo deleted successfully');
      setActionModalVisible(false);
      await fetchDemoLinks(); // refresh table
    } else {
      toast.error(res?.data?.message || 'Failed to delete demo');
    }
  } catch (err) {
    console.error('Delete demo error', err);
    toast.error(err?.response?.data?.message || 'Error deleting demo');
  } finally {
    setDeleteLoading(false);
  }
};


// edit modal state
const [editModalVisible, setEditModalVisible] = useState(false);
const [editLoading, setEditLoading] = useState(false);
const [editRecord, setEditRecord] = useState(null);



const openEditModal = (record) => {
  // Normalize date strings to Day.js with strict 'DD-MM-YYYY' format
  const normalized = {
    ...record,
    DemoDate: record?.DemoDate
      ? dayjs(record.DemoDate, 'DD-MM-YYYY', true)
      : null,
    BatchStartDate: record?.BatchStartDate
      ? dayjs(record.BatchStartDate, 'DD-MM-YYYY', true)
      : null,
  };

  setEditRecord(normalized);
  setEditModalVisible(true);
};

const handleEditFieldChange = (field, value) => {
  setEditRecord((prev) => ({ ...prev, [field]: value }));
};


const handleUpdateDemo = async () => {
  if (!editRecord) return;
  setEditLoading(true);

  try {
    const payloadItem = {
      id: editRecord.id ?? editRecord.key ?? null,
      DemoDate: editRecord.DemoDate
        ? editRecord.DemoDate.format('DD-MM-YYYY')
        : undefined,
      Time: editRecord.Time ?? '',
      BatchStartDate: editRecord.BatchStartDate
        ? editRecord.BatchStartDate.format('DD-MM-YYYY')
        : undefined,
      BatchDay: editRecord.BatchDay ?? '',
      BatchTime: editRecord.BatchTime ?? '',
      DemoLink: editRecord.DemoLink ?? '',
      DemoRecordingLink: editRecord.DemoRecordingLink ?? '',
    };

    const url = `${process.env.REACT_APP_API_URL}/demolinks/update-demolinks-multiple`;
    const res = await axios.put(
      url,
      { items: [payloadItem] },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 20000,
      }
    );

    if (res?.data?.success || res?.status === 200) {
      toast.success(res?.data?.message || 'Demo updated successfully');
      setEditModalVisible(false);
      setEditRecord(null);
      await fetchDemoLinks();
    } else {
      toast.error(res?.data?.message || 'Failed to update demo');
    }
  } catch (err) {
    console.error('Update demo error', err);
    toast.error(err?.response?.data?.message || 'Error updating demo');
  } finally {
    setEditLoading(false);
  }
};




  return (
    <div className="w-full py-4 mx-auto bg-white shadow-sm px-7 sm:px-6 lg:px-8 rounded-xl">
      <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              className="!mb-6 !ml-4"
              toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
            />

      <Title level={3} className="!text-3xl !font-bold text-gray-800 tracking-tight mb-3 border-b border-gray-200 pb-2">Demo Dates</Title>
      <Paragraph className="mb-6 text-base text-gray-700">
        Manage available demo dates and batches. You can add new demo dates using the <strong>Add Demo</strong> button.
        Use the month filter to narrow down options.
      </Paragraph>

      {error && <Alert type="error" message="Error" description={error} showIcon closable className="mb-6" />}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="font-bold text-gray-700 text-md">Filter by Month</label>
          <Select
            allowClear
            placeholder={<span style={{ color: '#4b5563' }}>Select Month</span>}
            value={filterMonth}
            onChange={(v) => setFilterMonth(v || null)}
            style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
            options={months.map((m) => ({ label: m, value: m }))}
          />
        </div>

        {/* <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Search</label>
          <Search
            placeholder="Search links / time"
            allowClear
            onSearch={(v) => setSearchText(v)}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ minWidth: 300 }}
            value={searchText}
            enterButton={false}
          />
        </div> */}

    <div className="ml-auto ">
  <Button
    type="primary"
    onClick={openAddModal}
    icon={<PlusOutlined className="text-lg font-extrabold" />}
    className=" !bg-blue-700 !hover:bg-blue-900 !text-white !font-bold text-lg !px-7 !py-7 !rounded-md !shadow-lg !shadow-blue-300/50 !transition-all !duration-300 !flex !items-center !gap-2"
  >
   
    Add Demo
  </Button>
</div>



      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey={(r) => r.key}
          loading={loading}
          pagination={{ pageSize: 10, total: filtered.length }}
          scroll={{ x: 1600 }}
          className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table-dates"
        />
      </div>

      {/* Add Demo Modal */}
      <Modal
        title={<span className="mb-6 text-lg font-bold">ADD DEMO DATE</span>}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        width={940}
        centered
      >
        <div className="space-y-6">
          <Row gutter={12}>
            <Col span={12}>
              <label className="block mb-1 text-base font-medium">Demo Date</label>
              <DatePicker
                value={demoDate}
                onChange={(d) => setDemoDate(d)}
                format={DATE_FORMAT}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <label className="block mb-1 text-base font-medium">Time (e.g. 06:00 PM to 08:00 PM)</label>
              <Input value={timeText} onChange={(e) => setTimeText(e.target.value)} placeholder="Time range" />
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <label className="block mb-1 text-base font-medium">Batch Start Date</label>
              <DatePicker
                value={batchStartDate}
                onChange={(d) => setBatchStartDate(d)}
                format={DATE_FORMAT}
                style={{ width: '100%' }}
              />
            </Col>
           
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <label className="block mb-1 text-base font-medium">Batch Day</label>
              <Select value={batchDay} onChange={(v) => setBatchDay(v)} placeholder="MORNING / EVENING" style={{ width: '100%' }}>
                <Option value="MORNING">MORNING</Option>
                <Option value="EVENING">EVENING</Option>
              </Select>
            </Col>
            <Col span={16}>
              <label className="block mb-1 text-base font-medium">Batch Time (e.g. 07:00 AM to 08:00 AM)</label>
              <Input value={batchTime} onChange={(e) => setBatchTime(e.target.value)} placeholder="Batch time range" />
            </Col>
          </Row>

          <div>
            <label className="block mb-1 text-base font-medium">Demo Link</label>
            <TextArea value={demoLink} onChange={(e) => setDemoLink(e.target.value)} rows={2} placeholder="https://..." />
          </div>

          <div>
            <label className="block mb-1 text-base font-medium">Demo Recording Link (optional)</label>
            <TextArea value={demoRecordingLink} onChange={(e) => setDemoRecordingLink(e.target.value)} rows={2} placeholder="https://..." />
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button onClick={() => setAddModalVisible(false)} disabled={addLoading}>Cancel</Button>
            <Button type="primary" onClick={handleAddDemo} disabled={addLoading}>
              {addLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Adding...</span> : 'Add Demo'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
  title={
    <span className="text-lg font-bold">
     DELETE DEMO DATE FOR {safe(selectedDemo?.DemoDate)} 
    </span>
  }
  open={actionModalVisible}
  onCancel={() => setActionModalVisible(false)}
  footer={null}
  centered
  width={560}
>
  <div className="p-3 space-y-4 text-center">

    <p className="mt-5 text-base text-gray-700">
      <span>Batch Start Date : <strong> {safe(selectedDemo?.BatchStartDate)} </strong></span> <br />
      <span>Batch Day : <strong> {safe(selectedDemo?.BatchDay)}</strong></span>
    </p>
    
    <p className="text-base text-gray-700">
      Are you sure you want to delete this demo date? This action cannot be undone.
    </p>

    <Button
      type="primary"
      danger
      loading={deleteLoading}
      onClick={handleDeleteDemo}
      className="!bg-red-600 hover:!bg-red-700 !border-none  !text-white !font-semibold !px-6 !py-4"
    >
      {deleteLoading ? 'Deleting...' : 'Delete Demo Date'}
    </Button>
  </div>
</Modal>


<Modal
  title={<span className="text-lg font-bold">EDIT DEMO DATE</span>}
  open={editModalVisible}
  onCancel={() => { setEditModalVisible(false); setEditRecord(null); }}
  footer={null}
  width={960}
  centered
>
  {editRecord ? (
    <div className="space-y-6">
      <Row gutter={12}>
        <Col span={12}>
          <label className="block mb-1 text-base font-medium">Demo Date</label>
          <DatePicker
            value={editRecord.DemoDate ?? null}
            onChange={(d) => handleEditFieldChange('DemoDate', d)}
            format={DATE_FORMAT}
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={12}>
          <label className="block mb-1 text-base font-medium">Time (e.g. 06:00 PM to 08:00 PM)</label>
          <Input
            value={editRecord.Time ?? editRecord.Time ?? ''}
            onChange={(e) => handleEditFieldChange('Time', e.target.value)}
            placeholder="Time range"
          />
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <label className="block mb-1 text-base font-medium">Batch Start Date</label>
          <DatePicker
            value={editRecord.BatchStartDate ?? null}
            onChange={(d) => handleEditFieldChange('BatchStartDate', d)}
            format={DATE_FORMAT}
            style={{ width: '100%' }}
          />
        </Col>
       
      </Row>

      <Row gutter={12}>
        <Col span={8}>
          <label className="block mb-1 text-base font-medium">Batch Day</label>
          <Select
            value={editRecord.BatchDay ?? null}
            onChange={(v) => handleEditFieldChange('BatchDay', v)}
            placeholder="MORNING / EVENING"
            style={{ width: '100%' }}
          >
            <Option value="MORNING">MORNING</Option>
            <Option value="EVENING">EVENING</Option>
          </Select>
        </Col>
        <Col span={16}>
          <label className="block mb-1 text-base font-medium">Batch Time (e.g. 07:00 AM to 08:00 AM)</label>
          <Input
            value={editRecord.BatchTime ?? ''}
            onChange={(e) => handleEditFieldChange('BatchTime', e.target.value)}
            placeholder="Batch time range"
          />
        </Col>
      </Row>

      <div>
        <label className="block mb-1 text-base font-medium">Demo Link</label>
        <TextArea
          value={editRecord.DemoLink ?? ''}
          onChange={(e) => handleEditFieldChange('DemoLink', e.target.value)}
          rows={2}
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block mb-1 text-base font-medium">Demo Recording Link </label>
        <TextArea
          value={editRecord.DemoRecordingLink ?? ''}
          onChange={(e) => handleEditFieldChange('DemoRecordingLink', e.target.value)}
          rows={2}
          placeholder="https://..."
        />
      </div>

      <div className="flex items-center justify-end gap-3 mt-2">
        <Button onClick={() => { setEditModalVisible(false); setEditRecord(null);} } disabled={editLoading}>Cancel</Button>
        <Button type="primary" onClick={handleUpdateDemo} disabled={editLoading}>
          {editLoading ? <span className="inline-flex items-center gap-2"><Spin size="small" /> Updating...</span> : 'Update Demo Date'}
        </Button>
      </div>
    </div>
  ) : (
    <div className="py-8 text-center"><Spin /></div>
  )}
</Modal>


    </div>
  );
}

export default DemoLinks